import { useNavigate } from 'react-router-dom';
import { theme } from '../theme.js';
import { useEquipmentHistory } from '../lib/queries.js';
import { actorLabel } from '../lib/people.js';
import { StatusPill } from './StatusPill.js';
import { Icon } from './Icon.js';

/**
 * One piece of equipment's results across past inspections, newest first —
 * e.g. to see whether "broken" is new or has been reported for weeks.
 * `excludeItemId` hides the check you're currently looking at.
 */
export function EquipmentHistory({
  orgId, equipmentId, excludeItemId, title = 'Previous inspections',
}: { orgId: string; equipmentId: string; excludeItemId?: string; title?: string }) {
  const nav = useNavigate();
  const { data = [], isLoading } = useEquipmentHistory(orgId, equipmentId);
  const entries = data.filter((e) => e.itemId !== excludeItemId);

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.textDim, marginBottom: 6 }}>{title}</div>
      {isLoading && <Muted>Loading…</Muted>}
      {!isLoading && entries.length === 0 && <Muted>No earlier results for this item.</Muted>}
      {entries.map((e) => (
        <button key={e.itemId} onClick={() => nav(`/checks/${e.runId}/items/${e.itemId}`)} style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', textAlign: 'left',
          padding: '9px 0', background: 'none', border: 'none', cursor: 'pointer', fontFamily: theme.font,
          borderTop: `1px solid ${theme.border}`,
        }}>
          <div style={{ width: 54, flexShrink: 0, fontSize: 12, color: theme.textMuted, paddingTop: 2 }}>
            {new Date(e.periodStart).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <StatusPill status={e.status} size="sm" />
              {e.flagged && <Icon name="flag" size={12} style={{ color: theme.status.danger.dot }} />}
              {e.runStatus === 'draft' && <span style={{ fontSize: 11, color: theme.textMuted }}>(in progress)</span>}
            </div>
            {e.note && <div style={{ fontSize: 13, color: theme.text, marginTop: 3 }}>{e.note}</div>}
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
              {[
                `${e.assetName}${e.sectionName ? ` › ${e.sectionName}` : ''}`,
                actorLabel(e.checkedByName, e.checkedByActive),
                e.externalRef && `Ref ${e.externalRef}`,
                e.photoCount > 0 && `${e.photoCount} photo${e.photoCount > 1 ? 's' : ''}`,
              ].filter(Boolean).join(' · ')}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 13, color: theme.textMuted, padding: '4px 0' }}>{children}</div>;
}
